import * as React from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useRouteContext } from '@tanstack/react-router';
import { offerUnits } from '@ooh/contracts';
import type {
  EndpointDef,
  callEndpoint,
  OfferUnitAllResponse,
  OfferUnitFilterOptionsResponse,
  OfferUnitFilters,
  OfferUnitListItem,
  OfferUnitListPageResponse,
  OfferUnitSortKey,
} from '@ooh/contracts';
import { offerUnitsQueryKeyPrefix } from '@ooh/screens';
import { offerAvailabilityKeys, offerKeys } from '../../api/queries';

export { offerAvailabilityKeys, offerKeys };

export interface WithCallWithAuth {
  callWithAuth: <TDef extends EndpointDef>(
    def: TDef,
    request: Parameters<typeof callEndpoint<TDef>>[1],
  ) => ReturnType<typeof callEndpoint<TDef>>;
}

export interface OfferUnitsServerSort {
  sortBy: OfferUnitSortKey;
  sortDir: 'asc' | 'desc';
}

const OFFER_UNITS_PAGE_SIZE = 50;

export const offerUnitQueries = {
  /**
   * Root prefix for every offer-units query. Mutation invalidation targets this
   * prefix so it fuzzy-matches every paginated `list` page.
   */
  all: offerUnitsQueryKeyPrefix,
  list: (
    offerId: number,
    sort: OfferUnitsServerSort,
    filters?: OfferUnitFilters,
    decisionVersionId?: number,
  ) =>
    [
      ...offerUnitQueries.all(offerId),
      'list',
      JSON.stringify(sort),
      JSON.stringify(filters ?? {}),
      String(decisionVersionId ?? ''),
    ] as const,
  /**
   * queryOptions for the whole-offer all-units fetch (`GET /offers/:offerId/units/all`).
   * Keyed under the `all(offerId)` prefix so existing mutation invalidation
   * (`invalidateQueries({ queryKey: all(offerId) })`) also refreshes it.
   */
  allUnits: (offerId: number, auth: WithCallWithAuth, enabled: boolean) =>
    ({
      queryKey: [...offerUnitQueries.all(offerId), 'all'] as const,
      queryFn: (): Promise<OfferUnitAllResponse> =>
        auth.callWithAuth(offerUnits.listAll, { offerId }),
      enabled,
    }) as const,
  /**
   * queryOptions for the offer-scoped filter options
   * (`GET /offers/:offerId/units/filter-options`). Keyed under the `all(offerId)`
   * prefix so mutation invalidation also refreshes the offer's option lists.
   */
  filterOptions: (offerId: number, auth: WithCallWithAuth) =>
    ({
      queryKey: [...offerUnitQueries.all(offerId), 'filter-options'] as const,
      queryFn: (): Promise<OfferUnitFilterOptionsResponse> =>
        auth.callWithAuth(offerUnits.filterOptions, { offerId }),
    }) as const,
  /**
   * queryOptions for a cheap grand-total fetch — a single first page
   * (`pageSize: 1`, no `filters`) whose only purpose is reading
   * `meta.totalCount`, the offer's true unfiltered unit count. Keyed under the
   * `all(offerId)` prefix so mutation invalidation also refreshes it.
   *
   * Deliberately NOT the whole-offer `listAll`/`allUnits` fetch — offers can
   * hold thousands of units, so this avoids loading every row just to read a
   * count. Callers should only enable this when a filter is active; when
   * unfiltered, `useOfferUnits`' own `totalCount` already IS the grand total.
   */
  grandTotal: (offerId: number, auth: WithCallWithAuth, enabled: boolean) =>
    ({
      queryKey: [...offerUnitQueries.all(offerId), 'grand-total'] as const,
      queryFn: (): Promise<OfferUnitListPageResponse> =>
        auth.callWithAuth(offerUnits.list, { offerId, pageSize: 1 }),
      enabled,
    }) as const,
};

export function useEffectiveAuth(auth?: WithCallWithAuth): WithCallWithAuth {
  const routeContext = useRouteContext({ from: '__root__' });
  return auth ?? (routeContext as { auth: WithCallWithAuth }).auth;
}

export interface UseOfferUnitsResult {
  allRows: OfferUnitListItem[];
  totalCount: number;
  hasNextPage: boolean;
  /**
   * Referentially stable across renders (wrapped in `useCallback`) — required
   * by `useTableVirtualizer`'s sentinel effect, whose dependency array would
   * otherwise recreate its `IntersectionObserver` on every render.
   */
  fetchNextPage: () => Promise<unknown>;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Cursor-based infinite query over `GET /offers/:offerId/units`.
 *
 * Pages are flattened into `allRows`; `totalCount` is the server's filtered
 * count (from `meta.totalCount`). Mirrors the catalog `useMediaUnitsPage`
 * infinite-query wiring.
 */
export function useOfferUnits(
  offerId: number,
  options: {
    sort?: OfferUnitsServerSort;
    filters?: OfferUnitFilters;
    decisionVersionId?: number;
    auth?: WithCallWithAuth;
  } = {},
): UseOfferUnitsResult {
  const sort = options.sort ?? { sortBy: 'sortOrder', sortDir: 'asc' };
  const { filters, decisionVersionId, auth } = options;
  const effectiveAuth = useEffectiveAuth(auth);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError } =
    useInfiniteQuery<
      OfferUnitListPageResponse,
      Error,
      { pages: OfferUnitListPageResponse[] },
      readonly [string, number, ...string[]],
      string | undefined
    >({
      queryKey: offerUnitQueries.list(offerId, sort, filters, decisionVersionId),
      queryFn: ({ pageParam }) =>
        effectiveAuth.callWithAuth(offerUnits.list, {
          offerId,
          cursor: pageParam,
          pageSize: OFFER_UNITS_PAGE_SIZE,
          sortBy: sort.sortBy,
          sortDir: sort.sortDir,
          ...(decisionVersionId == null ? {} : { decisionVersionId }),
          ...(filters ? { filters } : {}),
        }),
      placeholderData: (prev) => prev,
      initialPageParam: undefined,
      getNextPageParam: (lastPage) => lastPage.meta.nextCursor ?? undefined,
    });

  const allRows = (data?.pages ?? []).flatMap((page) => page.data);
  const totalCount = data?.pages[0]?.meta.totalCount ?? 0;

  // Stable identity — see UseOfferUnitsResult.fetchNextPage doc comment.
  const stableFetchNextPage = React.useCallback(() => fetchNextPage(), [fetchNextPage]);

  return {
    allRows,
    totalCount,
    hasNextPage,
    fetchNextPage: stableFetchNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  };
}

export interface UseOfferUnitsAllResult {
  /** Every unit of the offer (unpaginated/unfiltered), or `undefined` until fetched. */
  data: OfferUnitAllResponse | undefined;
  isLoading: boolean;
}

/**
 * Whole-offer all-units fetch for affordances that must cover the entire offer
 * regardless of the paginated/filtered table view — the map overlay and the
 * "Pobierz specyfikacje" bulk download.
 *
 * `enabled` gates the network call for perf: offers can hold thousands of units,
 * so callers should only enable it when the map is visible or bulk download is
 * available.
 */
export function useOfferUnitsAll(
  offerId: number,
  options: { enabled: boolean; auth?: WithCallWithAuth },
): UseOfferUnitsAllResult {
  const effectiveAuth = useEffectiveAuth(options.auth);
  const { data, isLoading } = useQuery(
    offerUnitQueries.allUnits(offerId, effectiveAuth, options.enabled),
  );
  return { data, isLoading };
}

export interface UseOfferUnitFilterOptionsResult {
  data: OfferUnitFilterOptionsResponse | undefined;
  isLoading: boolean;
}

/**
 * Offer-scoped filter-options fetch — the distinct filter values present among
 * THIS offer's units. Feeds the units-tab filter-panel dropdowns so they reflect
 * only the offer's inventory rather than the whole catalog.
 */
export function useOfferUnitFilterOptions(
  offerId: number,
  auth?: WithCallWithAuth,
): UseOfferUnitFilterOptionsResult {
  const effectiveAuth = useEffectiveAuth(auth);
  const { data, isLoading } = useQuery(offerUnitQueries.filterOptions(offerId, effectiveAuth));
  return { data, isLoading };
}

export interface UseOfferUnitsGrandTotalResult {
  /** The offer's true unfiltered unit count, or `undefined` until fetched/enabled. */
  totalCount: number | undefined;
  isLoading: boolean;
}

/**
 * Cheap grand-total fetch for the units-tab footer — see
 * `offerUnitQueries.grandTotal` for why this avoids the whole-offer fetch.
 * Callers should pass `enabled: true` only when a filter is currently active.
 */
export function useOfferUnitsGrandTotal(
  offerId: number,
  options: { enabled: boolean; auth?: WithCallWithAuth },
): UseOfferUnitsGrandTotalResult {
  const effectiveAuth = useEffectiveAuth(options.auth);
  const { data, isLoading } = useQuery(
    offerUnitQueries.grandTotal(offerId, effectiveAuth, options.enabled),
  );
  return { totalCount: data?.meta.totalCount, isLoading };
}
